import { CloseFullscreen, OpenInFullRounded } from '@mui/icons-material'
import GroupIcon from '@mui/icons-material/Group'
import { Chip, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { ITosExpense } from '../../definitions/ServiceExpense'
import { ChartDataWithUnit } from '../charts/ChartsHelper'
import { HorizontalBarChartResult } from '../charts/HorizontalBarChartResult'
import LabeledChart from '../controls/data_visualization/LabeledChart'
import useCommand from '../hooks/useCommand'
import useQuery, { buildQueryKey, QueryStatus } from '../hooks/useQuery'
import CustomIcon from '../icons/CustomIcon'

interface P {
    contractId: number
    title: string
    subtitle: string
    startDate: string
    endDate: string
    icon?: string
    changeViewFunction?: (view: string) => void
    triggerSnackbarError?: (view: string) => void
    updateDataFlag: boolean
    viewMode: string
    setViewMode?: (name: string) => void
    setExportMenu: (items: string[]) => void

}

function TotalExpenseByTosSection({ contractId, title, subtitle, startDate, endDate, icon, changeViewFunction, triggerSnackbarError, updateDataFlag, viewMode, setViewMode, setExportMenu }: P) {
    const theme = useTheme()

    const [expenseByTosData, setExpenseByTosData] = useState<{ data: ChartDataWithUnit<'bar', ITosExpense['value'][], string>, status: QueryStatus, refetch?: () => void }>({
        data: {
            datasets: [
                {
                    data: [],
                    unit: '$',
                    backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                }
            ],
            labels: []
        },
        status: 'loading'
    })

    const { status: responseStatus, refetch: refetch } = useQuery<ChartDataWithUnit<'bar', ITosExpense['value'][], string>>({
        queryKey: buildQueryKey('totalExpense', contractId, startDate, endDate),
        queryFn: async () => {
            setViewMode('pmpm')
            let result = await ApiClient.getContractTotalExpensePMPMByPeriod(contractId, startDate, endDate)

            if (!result) {
                result = await ApiClient.getContractTotalExpenseByPeriod(contractId, startDate, endDate)
                setViewMode('default')
            }

            setExpenseByTosData({
                data: {
                    datasets: [
                        {
                            data: result.map(item => item.value),
                            unit: '$',
                            backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                        }
                    ],
                    labels: result.map(item => item.name)
                },
                status: 'success',
                refetch: refetch
            })
            return {
                datasets: [
                    {
                        data: result.map(item => item.value),
                        unit: '$',
                        backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                    }
                ],
                labels: result.map(item => item.name)
            }
        },
        cache: false
    })

    const { execute: loadPMPMExpense, status: loadPMPMStatus } = useCommand({
        commandFn: async (): Promise<ITosExpense[]> => {
            let expenseByTosResponse = []
            setExpenseByTosData(
                prev => ({ ...prev, status: 'loading' })
            )

            switch (viewMode) {
                case 'default':
                    setViewMode('pmpm')
                    expenseByTosResponse = await ApiClient.getContractTotalExpensePMPMByPeriod(contractId, startDate, endDate)
                    break

                case 'pmpm':
                    setViewMode('default')
                    expenseByTosResponse = await ApiClient.getContractTotalExpenseByPeriod(contractId, startDate, endDate)
                    break
            }
            return expenseByTosResponse
        },
        onFinish: result => {
            if (result.response) {
                setExpenseByTosData({
                    data: {
                        datasets: [
                            {
                                data: result.response.map(item => item.value),
                                unit: '$',
                                backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                            }
                        ],
                        labels: result.response.map(item => item.name)
                    },
                    status: 'success'
                })
            }
        }
    })

    const handleChangeView = () => {
        changeViewFunction('normal')
    }

    if (responseStatus && responseStatus === 'error') triggerSnackbarError('There was an error during data fetch')

    useEffect(() => {
        refetch()
    }, [updateDataFlag])

    return (
        <Paper sx={{ height: '100%', padding: 3 }}>
            <Stack height={420}>
                <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Stack direction='row' alignItems='center' gap={1}>
                        { viewMode && (
                            <Chip size='small' variant='outlined' label={viewMode === 'pmpm' ? 'PMPM' : 'Total'} />
                        )}
                        <Stack>
                            <Typography variant='h6'>{title}</Typography>
                            <Typography variant='body2'>{subtitle}</Typography>
                        </Stack>
                    </Stack>
                    <Stack justifyContent='end' direction='row' gap='1'>
                        { viewMode && (
                            <Tooltip arrow title={viewMode === 'pmpm' ? 'Show total expenses view' : 'Show PMPM view'}>
                                <div>
                                    <IconButton size='small' onClick={loadPMPMExpense} disabled={responseStatus === 'loading' || loadPMPMStatus === 'loading'}>
                                        {viewMode === 'pmpm' ? <GroupIcon /> : <CustomIcon name='pmpm' />}
                                    </IconButton>
                                </div>
                            </Tooltip>
                        )}
                        { icon && (
                            <IconButton
                                size='small'
                                onClick={() => {
                                    handleChangeView()
                                    setExportMenu(null)
                                }}>
                                {icon === 'close' ? <CloseFullscreen /> : <OpenInFullRounded />}
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
                <LabeledChart
                    label='Total Rbce'>
                    <HorizontalBarChartResult<ITosExpense['value'][]>
                        data={expenseByTosData?.data}
                        status={expenseByTosData?.status}
                        theme={theme}
                        refetch={expenseByTosData?.refetch}
                    />
                </LabeledChart>
            </Stack>
        </Paper>

    )
}

export default TotalExpenseByTosSection
